import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Sparkles, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-4">Invalid Reset Link</h1>
          <p className="text-[#8A92A0] mb-6">This password reset link is invalid. Please request a new one.</p>
          <Link to="/auth">
            <button className="px-6 py-3 bg-[#EC008C] text-[#000000] font-semibold rounded-xl hover:bg-[#FF3399] transition-colors" data-testid="go-to-auth-btn">
              Go to Sign In
            </button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#EC008C]" />
          </div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-4">Password Reset!</h1>
          <p className="text-[#8A92A0] mb-6">Your password has been updated. You can now sign in with your new password.</p>
          <button
            onClick={() => navigate("/auth")}
            className="px-8 py-3 bg-[#EC008C] text-[#000000] font-semibold rounded-xl hover:bg-[#FF3399] transition-colors flex items-center gap-2 mx-auto"
            data-testid="go-to-signin-btn"
          >
            Sign In <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/auth")}
          className="flex items-center gap-2 text-[#8A92A0] hover:text-white mb-8 transition-colors text-sm"
          data-testid="back-to-auth-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Sign In
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#EC008C]/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#EC008C]" />
          </div>
        </div>

        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2" data-testid="reset-title">Set New Password</h1>
        <p className="text-[#8A92A0] mb-8">Enter your new password below.</p>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-[#D14A85]/10 border border-[#D14A85]/20 text-[#D14A85] text-sm" data-testid="reset-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              required
              minLength={6}
              className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-11 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#6A7280] hover:text-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
              className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 transition-colors"
              data-testid="confirm-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#EC008C] text-[#000000] font-semibold rounded-xl py-3 text-sm hover:bg-[#FF3399] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="reset-submit-btn"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
